/**
 * Instrument voices for the stems and the struck-metal SFX (08 §1 identity:
 * dry, physical — struck steel, ticking mechanisms, taiko-like lows, a bowed
 * drone). Every voice is `Instrument`-shaped (`sequencer.ts`): pure, returns
 * one hit with its natural tail normalised to peak 1, `vel` shapes timbre
 * (the sequencer applies it as level), `rng` only adds per-hit life so a
 * constant rng still yields the full sound. No Math.random.
 */
import type { Instrument } from './sequencer.js'

const TAU = Math.PI * 2

// ── DSP kit (biquads are RBJ; stable up to Nyquist unlike the SVF in synth.ts) ──

export type Filter = (x: number) => number

export function biquad(sr: number, kind: 'lp' | 'bp' | 'hp', hz: number, q: number): Filter {
  const w = TAU * Math.min(hz, sr * 0.49) / sr, cw = Math.cos(w), sw = Math.sin(w), a = sw / (2 * Math.max(0.1, q))
  let b0: number, b1: number, b2: number
  if (kind === 'lp') { b0 = (1 - cw) / 2; b1 = 1 - cw; b2 = b0 }
  else if (kind === 'hp') { b0 = (1 + cw) / 2; b1 = -(1 + cw); b2 = b0 }
  else { b0 = a; b1 = 0; b2 = -a }
  const a0 = 1 + a, a1 = -2 * cw, a2 = 1 - a
  const n0 = b0 / a0, n1 = b1 / a0, n2 = b2 / a0, d1 = a1 / a0, d2 = a2 / a0
  let x1 = 0, x2 = 0, y1 = 0, y2 = 0
  return (x) => { const y = n0 * x + n1 * x1 + n2 * x2 - d1 * y1 - d2 * y2; x2 = x1; x1 = x; y2 = y1; y1 = y; return y }
}

export function onePoleLp(sr: number, hz: number): Filter { let y = 0; const k = 1 - Math.exp(-TAU * hz / sr); return (x) => (y += k * (x - y)) }

/** Linear attack `a` s then exponential decay with time constant `d` s. */
export const envAD = (t: number, a: number, d: number): number => (t < a ? t / a : Math.exp(-(t - a) / d))

export function normalize(x: Float32Array, to = 1): Float32Array {
  let peak = 0
  for (let i = 0; i < x.length; i++) { const v = Math.abs(x[i]); if (v > peak) peak = v }
  if (peak > 0) { const g = to / peak; for (let i = 0; i < x.length; i++) x[i] *= g }
  return x
}

/** In-place add of `src` into `dst` at sample offset `at`, scaled by `g`. */
export function addInto(dst: Float32Array, src: Float32Array, at: number, g = 1): void {
  const n = Math.min(src.length, dst.length - at)
  for (let i = 0; i < n; i++) dst[at + i] += src[i] * g
}

const bip = (rng: () => number): number => rng() * 2 - 1

/** Half-sine pulse of width 1/(2·hz) — a unipolar click whose spectral peak sits near `hz`. */
export function pulse(sr: number, hz: number, amp = 1): Float32Array {
  const n = Math.max(3, Math.round(sr / (2 * hz)))
  const out = new Float32Array(n)
  for (let i = 0; i < n; i++) out[i] = Math.sin(Math.PI * i / n) * amp
  return out
}

/** Damped sine: `amp · sin(φ + ω t) · envAD`. */
export function ring(sr: number, hz: number, decayS: number, amp = 1, phase = 0, attackS = 0.0003, lenS = decayS * 4.5): Float32Array {
  const n = Math.round(lenS * sr), w = TAU * hz / sr
  const out = new Float32Array(n)
  for (let i = 0; i < n; i++) out[i] = Math.sin(phase + w * i) * envAD(i / sr, attackS, decayS) * amp
  return out
}

/** Filtered noise burst: `filt` shaped noise × envAD. Zero when `rng` is constant 0.5 (by design). */
export function burst(sr: number, lenS: number, attackS: number, decayS: number, filt: Filter, rng: () => number, amp = 1): Float32Array {
  const n = Math.round(lenS * sr)
  const out = new Float32Array(n)
  for (let i = 0; i < n; i++) out[i] = filt(bip(rng)) * envAD(i / sr, attackS, decayS) * amp
  return out
}

// ── voices ─────────────────────────────────────────────────────────────────

export interface Partial { ratio: number; amp: number; decayS: number }

/**
 * Struck metal by modal synthesis: inharmonic partials with per-partial decay,
 * a deterministic 3 ms clang plus a noise strike. `note` transposes in
 * semitones; `vel` brightens the upper partials and the strike.
 */
export const modalMetal = (partials: Partial[], baseHz: number): Instrument => (sr, vel, note, rng) => {
  const f0 = baseHz * Math.pow(2, note / 12)
  const maxDec = Math.max(...partials.map((p) => p.decayS))
  const out = new Float32Array(Math.round(Math.min(3, maxDec * 4.5) * sr))
  const bright = 0.55 + 0.45 * vel
  partials.forEach((p, k) => {
    const hz = f0 * p.ratio * (1 + 0.002 * bip(rng))
    if (hz > sr * 0.45) return
    addInto(out, ring(sr, hz, p.decayS, p.amp * (k === 0 ? 1 : bright), rng() * TAU, 0.0005, out.length / sr), 0)
  })
  addInto(out, ring(sr, Math.min(f0 * 7.3, sr * 0.4), 0.003, 0.9 + 0.3 * vel, 0, 0.0002), 0)
  addInto(out, burst(sr, 0.004, 0.0002, 0.001, biquad(sr, 'bp', Math.min(f0 * 4, sr * 0.4), 1), rng, 0.6 * vel), 0)
  return normalize(out)
}

/**
 * Mechanism tick: two unipolar clicks 3 ms apart (half-sine pulses with a faint
 * ring), spectral peak 2.2–4.8 kHz from `vel`. Per-hit ±8 % pitch and ±10 %
 * second-click level from `rng`.
 */
export const mechanismTick: Instrument = (sr, vel, _note, rng) => {
  const hz = (2200 + 2600 * vel) * (1 + 0.08 * bip(rng))
  const out = new Float32Array(Math.round(0.012 * sr))
  const click = (at: number, f: number, amp: number) => {
    addInto(out, pulse(sr, f, amp), at)
    addInto(out, ring(sr, f, 0.0005, 0.12 * amp, 0, 0.0001, 0.003), at + pulse(sr, f).length)
  }
  click(0, hz, 1)
  click(Math.round(0.003 * sr), hz * 1.15, 0.75 * (1 + 0.1 * bip(rng)))
  return normalize(out)
}

/**
 * Taiko-like low: sine with a −12 st pitch envelope over 80 ms, skin noise,
 * 0.6 s tail. `note` transposes; `vel` adds skin and a touch of drive.
 */
export const taikoLow = (hz: number): Instrument => (sr, vel, note, rng) => {
  const f = hz * Math.pow(2, note / 12)
  const n = Math.round(0.6 * sr)
  const out = new Float32Array(n)
  let ph = 0
  for (let i = 0; i < n; i++) {
    const t = i / sr
    const glide = Math.pow(2, Math.max(0, 1 - t / 0.08))         // 2× → 1× (−12 st) over 80 ms
    ph += TAU * f * glide / sr
    const body = Math.sin(ph) * envAD(t, 0.002, 0.13)
    out[i] = Math.tanh(body * (1 + 0.6 * vel)) / Math.tanh(1 + 0.6 * vel)
  }
  addInto(out, burst(sr, 0.03, 0.001, 0.008, onePoleLp(sr, 500), rng, 0.35 + 0.35 * vel), 0)
  return normalize(out)
}

/** Ride tick: 9 ms sizzle of three inharmonic 8–12 kHz partials plus high noise; `vel` → level and brightness. */
export const rideTick: Instrument = (sr, vel, _note, rng) => {
  const out = new Float32Array(Math.round(0.009 * sr))
  const tilt = 0.7 + 0.3 * vel
  addInto(out, ring(sr, 8400, 0.0025, 1, rng() * TAU, 0.0002, 0.009), 0)
  addInto(out, ring(sr, 10150, 0.002, 0.8 * tilt, rng() * TAU, 0.0002, 0.009), 0)
  addInto(out, ring(sr, 11700, 0.0015, 0.6 * tilt, rng() * TAU, 0.0002, 0.009), 0)
  addInto(out, burst(sr, 0.009, 0.0002, 0.002, biquad(sr, 'bp', 10000, 1.2), rng, 0.8), 0)
  return normalize(out)
}

/**
 * Bowed drone: two detuned saws through a resonant lowpass whose cutoff steps
 * (glided over 1.5 s) at `steps[].atS`, 5.5 Hz vibrato ±6 cents, bow noise.
 * Exactly `seconds · sr` samples; 2 s fade-in, 0.3 s fade-out.
 */
export function bowedDrone(sr: number, seconds: number, steps: { atS: number; cutoffHz: number }[], rng: () => number, hz = 55): Float32Array {
  const n = Math.round(seconds * sr)
  const out = new Float32Array(n)
  const sorted = [...steps].sort((a, b) => a.atS - b.atS)
  const cutoffAt = (t: number): number => {
    let c = sorted[0]?.cutoffHz ?? 400
    for (const s of sorted) {
      if (t < s.atS) break
      const k = Math.min(1, (t - s.atS) / 1.5)
      c = Math.exp(Math.log(c) * (1 - k) + Math.log(s.cutoffHz) * k)
    }
    return c
  }
  const saw = (ph: number): number => 2 * (ph / TAU - Math.floor(ph / TAU + 0.5))
  const bow = biquad(sr, 'bp', 1800, 0.8)
  let ph1 = 0, ph2 = 0, lo = 0, band = 0, cut = cutoffAt(0)
  for (let i = 0; i < n; i++) {
    const t = i / sr
    if ((i & 255) === 0) cut = cutoffAt(t)
    const vib = Math.pow(2, (6 / 1200) * Math.sin(TAU * 5.5 * t))
    ph1 += TAU * hz * vib / sr; ph2 += TAU * hz * 1.0023 * vib / sr
    const x = (saw(ph1) + saw(ph2) * 0.7) * 0.5 + bow(bip(rng)) * 0.06
    // Chamberlin SVF lowpass (cutoff ≤ 3.2 kHz keeps it stable), Q ≈ 3.
    const f = 2 * Math.sin(Math.PI * Math.min(0.2, cut / sr))
    lo += f * band; const hi = x - lo - 0.33 * band; band += f * hi
    const env = Math.min(1, t / 2) * Math.min(1, (seconds - t) / 0.3)
    out[i] = lo * env
  }
  return normalize(out, 0.9)
}

export const PRESETS: { bell: Instrument; anvil: Instrument; tick: Instrument; kick: Instrument; tom: Instrument; ride: Instrument } = {
  bell: modalMetal([{ ratio: 1, amp: 1, decayS: 0.35 }, { ratio: 2.0, amp: 0.45, decayS: 0.22 }, { ratio: 2.76, amp: 0.4, decayS: 0.18 }, { ratio: 4.07, amp: 0.2, decayS: 0.12 }, { ratio: 5.4, amp: 0.12, decayS: 0.08 }], 880),
  anvil: modalMetal([{ ratio: 1, amp: 1, decayS: 0.12 }, { ratio: 1.58, amp: 0.6, decayS: 0.09 }, { ratio: 2.24, amp: 0.4, decayS: 0.06 }, { ratio: 3.3, amp: 0.25, decayS: 0.04 }], 1400),
  tick: mechanismTick,
  kick: taikoLow(55),
  tom: taikoLow(48),
  ride: rideTick,
}
